import React, { Fragment, useEffect, useState } from "react";
import { Box, Container, Typography, Grid, Button } from "@material-ui/core";
import { emailValid, passwordValid } from "../lib/validation";
import { httpPost } from "../lib/apiClients";
import { AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";
import ButtonLoader from "../components/ButtonLoader";
import SectionHeader from "../components/SectionHeader";
import { Environment } from "../utils/environments";
export default function PasswordReset({ email }) {
  useEffect(() => {
    document.title = "パスワード再発行 | じぶん専用IT相談室";
  }, []);
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState<boolean | string>(false);
  const [authError, setAuthError] = useState<boolean>(false);
  const [resetError, setResetError] = useState<boolean>(false);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(true);
  const [passwordMatch, setPasswordMatch] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  /**
   * Handles the form submission for password reset.
   *
   * @param {Event} e - The event object from the form submission.
   * @returns {Promise<void>} A promise that resolves when the reset process is complete.
   */
  async function handleReset(e) {
    e.preventDefault();
    if (loading) {
      return;
    }
    setLoading(true);
    const authCode = e.target.elements[0].value.trim();
    const email = e.target.elements[1].value.trim();
    const password = e.target.elements[2].value.trim();
    const confirmPassword = e.target.elements[3].value.trim();
    setAuthError(false);
    setEmailError(false);
    setResetError(false);
    var isError = false;
    if (authCode == "") {
      setAuthError(true);
      setLoading(false);
      isError = true;
    }
    if (!emailValid(email)) {
      if (email.length > 319) {
        setEmailError("メールアドレスは、319文字以内で入力してください");
      } else {
        setEmailError("メールアドレス形式で入力してください");
      }
      setLoading(false);
      isError = true;
    }

    const isValid = passwordValid(password);
    setIsPasswordValid(isValid);
    if (!isValid) {
      setLoading(false);
      isError = true;
    }
    if (password !== confirmPassword) {
      setPasswordMatch(false);
      setLoading(false);
      isError = true;
    } else {
      setPasswordMatch(true);
    }
    if (isError) {
      return;
    }
    try {
      await httpPost(
        {
          pathName: "password-reset",
          bodyParams: {
            authCode: authCode,
            email: email,
            newPassword: password,
            newPasswordConfirm: confirmPassword,
          },
        },
        false
      ).then((response: AxiosResponse<any>) => {
        if (response.status == 200) {
          navigate("/password-reset-complete");
        }
      });
    } catch (error) {
      if (Environment.getBuildType() != "prod") {
        console.log(error);
      }
      if (error.response.status >= 400 && error.response.status <= 499) {
        setResetError(true);
      } else {
        navigate(`/error?code=${error.response?.status}`);
      }
    } finally {
      setLoading(false);
    }
  }
  return (
    <Fragment>
      <Box component={"section"} className="section bg-dots">
        <Container className="container" style={{ marginBottom: "50px" }}>
          <SectionHeader currentPage={"パスワード変更"} preHeading={"Reset"} />
          <Box className="text-center mobile-text-left">
            <Typography>
              入力いただいたアドレス宛にメールを送信しました。
              <br />
              <strong>
                メール文中にある「認証コード」とメールアドレス、新しいパスワードを入力してください。
              </strong>
              <br />
              <br />
              確認メールは「{email}」からお送りします。
              <br />
              メールが届かない場合は、迷惑メールフォルダに分類されていないか
              <br />
              ご確認の上再度お試しください。
              <br />
            </Typography>
          </Box>
        </Container>
        <Container className="container">
          <Box className="element-box">
            <form onSubmit={handleReset}>
              <Grid
                container
                spacing={2}
                alignItems="center"
                style={{ marginBottom: "20px" }}
              >
                <Grid item md={3} sm={4} xs={12}>
                  <label>
                    認証コード<span className="required">必須</span>
                  </label>
                </Grid>
                <Grid item md={6} sm={8} xs={12}>
                  <input
                    type="text"
                    placeholder="認証コード"
                    className="input-field"
                  />
                  {authError && (
                    <Typography className="error-message">
                      認証コードを入力してください
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                alignItems="center"
                style={{ marginBottom: "20px" }}
              >
                <Grid item md={3} sm={4} xs={12}>
                  <label>
                    メールアドレス<span className="required">必須</span>
                  </label>
                </Grid>
                <Grid item md={6} sm={8} xs={12}>
                  <input
                    max={319}
                    type="email"
                    placeholder="メールアドレス"
                    className="input-field"
                  />
                  {emailError && (
                    <Typography className="error-message">
                      {emailError}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                alignItems="center"
                style={{ marginBottom: "20px" }}
              >
                <Grid item md={3} sm={4} xs={12}>
                  <label>
                    新しいパスワード<span className="required">必須</span>
                  </label>
                </Grid>
                <Grid item md={6} sm={8} xs={12}>
                  <input
                    maxLength={12}
                    type="password"
                    placeholder="新しいパスワード"
                    className="input-field"
                  />
                  {!isPasswordValid && (
                    <Typography className="error-message">
                      パスワードを半角英数記号6文字以上12文字以内で入力してください
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                alignItems="center"
                style={{ marginBottom: "20px" }}
              >
                <Grid item md={3} sm={4} xs={12}>
                  <label>
                    新しいパスワード（確認）
                    <span className="required">必須</span>
                  </label>
                </Grid>
                <Grid item md={6} sm={8} xs={12}>
                  <input
                    maxLength={12}
                    type="password"
                    placeholder="新しいパスワード"
                    className="input-field"
                  />
                  {!passwordMatch && (
                    <Typography className="error-message">
                      新しいパスワードと確認用パスワードが一致しません
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  style={{ margin: "30px 0", textAlign: "center" }}
                >
                  {loading && <div className="button-overlay"></div>}
                  <button
                    className="classification-btn"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? <ButtonLoader /> : "パスワード再発行"}
                  </button>
                  {resetError && (
                    <Typography className="error-message">
                      パスワード再発行ができません。 <br />
                      ご不明な点がございましたら、下記のお問い合わせフォームより、お気軽に運営事務局までお問い合わせください」
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </form>
          </Box>
        </Container>
      </Box>
    </Fragment>
  );
}
